<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { useMutation } from '@/composables/api'
import { useDialogsStore } from '@/stores/dialogsStore'
import { invalidateCaseResources } from '@/utils/queryInvalidation'
import type { AmortizationCommitBody, AmortizationPage } from './types'

import BDialog from '@/components/base/BDialog/BDialog.vue'
import AmortizationSelectPage from './AmortizationSelectPage.vue'
import AmortizationSetupPage from './AmortizationSetupPage.vue'
import AmortizationFeedbackPage from './AmortizationFeedbackPage.vue'

// Singleton amortization ("split payment") dialog (ADA-4437 etapp 5) — replaces @old
// amortization. Opens via dialogsStore.amortization: single mode (a document, opens on Setup)
// or multi mode (candidate documents, opens on Select). The commit POST invalidates the query
// cache (replaces the legacy remove_document_ids splice). Feedback shows the plan's first
// payment day from the submitted payload — never a document mutation.
const { t } = useI18n()
const dialogsStore = useDialogsStore()
const route = useRoute()
const router = useRouter()
const queryClient = useQueryClient()

const model = computed({
  get: () => dialogsStore.amortization != null,
  set: (value) => {
    if (!value) dialogsStore.amortization = null
  },
})

const page = ref<AmortizationPage>('setup')
const feedback = ref<'success' | 'failed' | null>(null)
const chosenDate = ref<Date | null>(null)

// Choose the entry page ONCE on open: Select for multi mode, Setup for single mode. Watch only
// the open/closed transition — NOT the payload contents — because the Select step rewrites
// dialogsStore.amortization (adding params) and a content-watcher would then force page back to
// 'select', clobbering the emit('next') → 'setup' transition.
watch(
  () => dialogsStore.amortization != null,
  (isOpen) => {
    if (!isOpen) return
    const payload = dialogsStore.amortization
    page.value = payload?.multiple != null && payload.multiple.documents.length > 0 ? 'select' : 'setup'
  },
  { immediate: true }
)

const commitMutation = useMutation('post', '/domain-actions/cases/new_amortization/{resource_id}', {
  meta: { notifyOnSuccess: false },
})

async function confirm(payload: { body: AmortizationCommitBody; date: Date }) {
  const params = dialogsStore.amortization?.params
  if (params == null) return
  chosenDate.value = payload.date
  try {
    await commitMutation.mutateAsync({
      path: { resource_id: params.id },
      // @ts-expect-error Swagger lies — the new_amortization action requires the plan body.
      body: payload.body,
    })
    feedback.value = 'success'
  } catch {
    feedback.value = 'failed'
  }
  page.value = 'feedback'
  if (feedback.value !== 'success') return

  // The action creates customer--amortizations AND moves the case out of /cases — hence the
  // extra prefix. Fire-and-forget OUTSIDE the try: the plan already exists — a failing refetch
  // must not flip the feedback to 'failed' (query errors snackbar via the global handlers).
  void invalidateCaseResources(queryClient, ['/customer--amortization'])
}

// Legacy parity (beslut 4): after a successful plan, return to Home. Auto-invalidation refetches
// the overview so the new plan shows — no document removal needed. Navigation is a UX choice.
function close() {
  if (feedback.value === 'success' && route.name !== 'Home') {
    void router.push('/')
  }
  model.value = false
}

function afterLeave() {
  page.value = 'setup'
  feedback.value = null
  chosenDate.value = null
}
</script>

<template>
  <b-dialog
    v-model="model"
    :hide-header="page === 'feedback'"
    :hide-footer="true"
    :aria-label="t('common.split_your_payment')"
    @after-leave="afterLeave"
  >
    <template #title>{{ t('common.split_your_payment') }}</template>

    <!-- `disabled` turns off VWindow's user-driven navigation (touch swipe) — the steps advance
         only programmatically, unlike the swipeable Home tabs. -->
    <v-window
      v-model="page"
      disabled
    >
      <v-window-item value="select">
        <amortization-select-page @next="page = 'setup'" />
      </v-window-item>

      <v-window-item value="setup">
        <amortization-setup-page
          :loading="commitMutation.isPending.value"
          @back="page = 'select'"
          @confirm="confirm"
        />
      </v-window-item>

      <v-window-item value="feedback">
        <amortization-feedback-page
          :feedback="feedback"
          :date="chosenDate"
          @close="close"
        />
      </v-window-item>
    </v-window>
  </b-dialog>
</template>
